import React, { createContext, useContext, useState, useEffect } from 'react';
import { AuthState, AuthContextType, AuthUser } from '../types/auth';
import { getAuthService, IAuthService } from '../services';

const STORAGE_KEY = 'auth_state';

const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  token: null
};

// Create the context with a default value that matches the shape
const AuthContext = createContext<AuthContextType>({
  ...initialState,
  login: () => {},
  logout: () => {}
});

// Create the provider component
function AuthProviderComponent({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>(() => {
    const storedState = localStorage.getItem(STORAGE_KEY);
    return storedState ? JSON.parse(storedState) : initialState;
  });

  const [authService, setAuthService] = useState<IAuthService | null>(null);

  // Initialize auth service
  useEffect(() => {
    let mounted = true;

    const initService = async () => {
      try {
        const service = await getAuthService();
        if (mounted) {
          setAuthService(service);
        }
      } catch (error) {
        console.error('Failed to initialize auth service:', error);
      }
    };

    initService();

    return () => {
      mounted = false;
    };
  }, []);

  // Initialize auth state
  useEffect(() => {
    if (!authService) return;

    let mounted = true;

    const initializeAuth = async () => {
      try {
        const session = await authService.getCurrentSession();
        if (mounted && session) {
          setState({
            isAuthenticated: true,
            user: session.user,
            token: session.token
          });
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
        if (mounted) {
          setState(initialState);
        }
      }
    };

    initializeAuth();

    return () => {
      mounted = false;
    };
  }, [authService]);

  const login = async (token: string, user: AuthUser, rememberMe: boolean) => {
    const newState = {
      isAuthenticated: true,
      user,
      token
    };
    setState(newState);

    if (rememberMe) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
    }
  };

  const logout = async () => {
    if (authService) {
      authService.signOut();
    }
    setState(initialState);
    localStorage.removeItem(STORAGE_KEY);
  };

  const value = {
    ...state,
    login,
    logout
  };

  // Show loading state while auth service is initializing
  if (!authService) {
    return null; // Or a loading spinner component
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// Export the hook
function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

// Export the provider component
const AuthProvider = AuthProviderComponent;

export { AuthProvider, useAuth }; 